import React, { useState, useEffect, useContext } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faMobileAlt, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { isIOS, isAndroid } from '../../utils/deviceDetection';
import { ThemeContext } from '../../context/ThemeProvider';
import { landingPageConfig } from '../../../app_constant/landingPageConfig';

const MobileAppBanner = () => {
  const { theme } = useContext(ThemeContext);
  const [isVisible, setIsVisible] = useState(false);
  const [downloadLink, setDownloadLink] = useState(null);

  useEffect(() => {
    const isDismissed = sessionStorage.getItem('mobileAppBannerDismissed');
    if (isDismissed === 'true') return;

    const appConfig = landingPageConfig?.appConfig;
    const ios = isIOS();
    const android = isAndroid();

    if (ios) {
      // Use Safari's native Smart App Banner — it shows "OPEN" if installed, "GET" if not
      const appStoreId = appConfig?.appStoreId;
      if (appStoreId) {
        const meta = document.createElement('meta');
        meta.name = 'apple-itunes-app';
        meta.content = `app-id=${appStoreId}`;
        document.head.appendChild(meta);
        // Don't show custom banner on iOS — Safari's native banner handles it
        return () => {
          document.head.removeChild(meta);
        };
      }
    } else if (android) {
      const androidPackage = appConfig?.androidPackage;
      const downloadLinks = appConfig?.downloadLinks || [];
      const playStoreLink = downloadLinks.find(link => link.store === 'Google');

      if (androidPackage && playStoreLink && playStoreLink.url !== '#') {
        // Intent URL: opens app if installed, falls back to Play Store
        const intentUrl = `intent://open#Intent;package=${androidPackage};scheme=https;S.browser_fallback_url=${encodeURIComponent(playStoreLink.url)};end`;
        setDownloadLink(intentUrl);
        setIsVisible(true);
      }
    }
  }, []);

  const handleDismiss = () => {
    sessionStorage.setItem('mobileAppBannerDismissed', 'true');
    setIsVisible(false);
  };

  const handleOpenClick = () => {
    if (downloadLink) {
      window.location.href = downloadLink;
    }
  };

  if (!isVisible) return null;

  const bannerStyles = {
    container: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 60,
      backgroundColor: theme?.primary?.blue?.[600],
      color: theme?.neutral?.absolute_white,
      padding: '12px 16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '12px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
      borderBottom: `1px solid ${theme?.primary?.blue?.[700]}`,
    },
    content: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      flex: 1,
      minWidth: 0,
    },
    icon: {
      fontSize: '18px',
      flexShrink: 0,
      color: theme?.neutral?.absolute_white,
    },
    text: {
      fontSize: '14px',
      fontWeight: '500',
      lineHeight: '1.4',
      flex: 1,
      minWidth: 0,
    },
    openButton: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      backgroundColor: theme?.neutral?.absolute_white,
      color: theme?.primary?.blue?.[600],
      padding: '8px 16px',
      borderRadius: '6px',
      fontSize: '13px',
      fontWeight: '600',
      border: 'none',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      flexShrink: 0,
      whiteSpace: 'nowrap',
    },
    dismissButton: {
      backgroundColor: 'transparent',
      border: 'none',
      color: theme?.neutral?.absolute_white,
      cursor: 'pointer',
      padding: '4px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '4px',
      transition: 'background-color 0.2s ease',
      flexShrink: 0,
      width: '24px',
      height: '24px',
    },
  };

  return (
    <div style={bannerStyles.container}>
      <div style={bannerStyles.content}>
        <FontAwesomeIcon icon={faMobileAlt} style={bannerStyles.icon} />
        <span style={bannerStyles.text}>
          Get our app for a better experience
        </span>
      </div>
      {downloadLink && (
        <button
          onClick={handleOpenClick}
          style={bannerStyles.openButton}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = '0.9';
            e.currentTarget.style.transform = 'scale(1.02)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = '1';
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          <FontAwesomeIcon icon={faExternalLinkAlt} style={{ fontSize: '12px' }} />
          Open App
        </button>
      )}
      <button
        onClick={handleDismiss}
        style={bannerStyles.dismissButton}
        aria-label="Dismiss banner"
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        <FontAwesomeIcon icon={faTimes} style={{ fontSize: '14px' }} />
      </button>
    </div>
  );
};

export default MobileAppBanner;
